import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import SubtitleTextBlock from 'components/Luxkit/TextBlocks/SubtitleTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import ResponsiveImage from '../ResponsiveImage'
import TextButton from 'components/Luxkit/Button/TextButton'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { dateDifference, intlFormatter } from 'lib/datetime/dateUtils'
import OfferUrgencyCountdownLabel from '../Labels/OfferUrgencyCountdownLabel'
import ClickableLink from '../Clickable/ClickableLink'
import PaneBody from '../Pane/PaneBody'

const CarouselContainer = styled.div`
  width: ${rem(300)};

  ${mediaQueryUp.tablet} {
    width: ${rem(560)};
  }
`

const DealsContainer = styled.div`
  display: flex;
  height: ${rem(115)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: ${rem(160)};
  }
`

const ColourContainer = styled.div`
  padding: ${rem(16)};
  width: ${rem(480)};
  height: 100%;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    width: ${rem(304)};
    height: 100%;
  }
`

const LogoContainer = styled.div`
  width: ${rem(64)};
  margin-bottom: ${rem(8)};

  ${mediaQueryUp.tablet} {
    width: ${rem(84)};
    margin-bottom: 0;
  }
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: ${rem(303)};
  height: ${rem(115)};

  ${mediaQueryUp.tablet} {
    width: ${rem(260)};
    height: ${rem(160)};
  }
`

const ButtonContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    position: absolute;
    z-index: 999;
    right: ${rem(20)};
    bottom: ${rem(20)};
  }
`

interface Props {
  backgroundColour: string
  logoImageId: string
  shipImageId: string
  heading: string
  subHeading: string
  endDate: string
  slug: string
}

function DealsTileItem(props: Props) {
  const { backgroundColour, logoImageId, shipImageId, heading, subHeading, endDate, slug } = props
  const urlDealPage = `/cruises/deal/${slug}`
  const [isHovered, setIsHovered] = useState(false)

  const shouldShowCountdown = useMemo(() => {
    const daysToShowCountdown = 7
    return dateDifference(new Date(endDate), new Date()).days < daysToShowCountdown
  }, [endDate])

  return <ClickableLink to={urlDealPage} preventSwipeClick>
    <CarouselContainer>
      <Group direction="vertical" gap={8}>
        <DealsContainer onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <ColourContainer style={{ backgroundColor: backgroundColour }}>
            <Group direction="vertical" verticalAlign="space-between" fullHeight>
              <LogoContainer>
                <ResponsiveImage
                  id={logoImageId}
                  src={logoImageId}
                  quality="eco"
                  role="presentation"
                  alt={heading}
                />
              </LogoContainer>
              <CSSBreakpoint max="mobile">
                <BodyTextBlock variant="small" colour="neutral-eight" weight="bold">
                  {heading}
                </BodyTextBlock>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <SubtitleTextBlock variant="subtitle2" colour="neutral-eight">
                  {heading}
                </SubtitleTextBlock>
              </CSSBreakpoint>
            </Group>
          </ColourContainer>
          <ImageContainer>
            {isHovered && <ButtonContainer>
              <TextButton kind="primary" variant="ghost" size="small">
                View all offers
              </TextButton>
            </ButtonContainer>}
            <ResponsiveImage
              fit="center"
              id={shipImageId}
              src={shipImageId}
              width={42}
              quality="eco"
              role="presentation"
              alt={heading}
            />
          </ImageContainer>
        </DealsContainer>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {subHeading}
          </BodyText>
          {!shouldShowCountdown && <BodyText variant="small">
            Offer ends on {intlFormatter(endDate, INTL_DMY_CASUAL_FORMAT)}
          </BodyText>}
        </Group>
      </Group>
      <PaneBody size="xs zero-x">
        {shouldShowCountdown && <OfferUrgencyCountdownLabel
          endDate={endDate}
        />}
      </PaneBody>
    </CarouselContainer>
  </ClickableLink>
}

export default DealsTileItem
